import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import Message from './message';
import Feedback from './feedback';
import ThinkingPopup from './thinking-popup';
import { AuthRequired, IDialogItem, ThinkingContent } from 'app/concierge/types';
import { IDomain } from 'store/conciergeSlice';
import useBundleTranslation from 'i18n';
import { findPreviousRequest, findResolution } from 'app/concierge/tools';
import IconMi from 'components/common/icon/IconMi';
import { merge } from 'lodash';
import { ICustomStyles } from '../index';
import defaultStyles from './styles';
import { useSelector } from 'react-redux';
import toast from 'app/toast';

interface IProps {
    dialog: IDialogItem[];
    id: number;
    loading: boolean;
    showSearchRequest?: boolean;
    botIcon?: string;
    loadMore?: boolean;
    onUpdateDialog?: (updatedDialog: IDialogItem[]) => void;
    useInfoButton?: boolean;
    onResendRequest?: (excludedTools: string[]) => void;
    onExcludedToolsChange?: (excludedTools: string[]) => void;
    onAction?: (
        action: string,
        payload: any,
    ) => Promise<{ success: boolean; message?: string }> | { success: boolean; message?: string };
}

export default function ActiveChat({
    dialog,
    id,
    loading,
    showSearchRequest,
    botIcon,
    loadMore,
    onUpdateDialog,
    useInfoButton,
    onResendRequest,
    onAction,
}: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackType, setFeedbackType] = useState('up' as 'up' | 'down');
    const [selectedMessage, setSelectedMessage] = useState<number | null>(null);
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [selectedQuestion, setSelectedQuestion] = useState('');
    const [showThinking, setShowThinking] = useState(false);
    const [selectedThinkingContent, setSelectedThinkingContent] = useState<ThinkingContent | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;
    const authorizedTools = useSelector((state: any) => state.concierge.authorizedTools) as string[];
    const authorizedToolsRef = useRef<string[]>(authorizedTools);
    authorizedToolsRef.current = authorizedTools;
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const selectedDomains = useSelector((state: any) => state.concierge.selectedDomains) as string[];

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.activeChat) as typeof defaultStyles;
    }, [customStyles]);

    const authRequiredToolNames = useMemo(() => {
        const names: string[] = [];
        dialog.forEach((dialogItem) => {
            dialogItem.parts.forEach((part) => {
                if (part.type === 'json' && part.jsonBlocks) {
                    part.jsonBlocks.forEach((block) => {
                        if (block.data_type === 'auth-required') {
                            (block as AuthRequired).data.forEach((authData) => {
                                if (!names.includes(authData.toolName)) {
                                    names.push(authData.toolName);
                                }
                            });
                        }
                    });
                }
            });
        });
        return names;
    }, [dialog]);

    const proceedButtonFilteredDomains = useMemo(() => {
        const activeAuthRequired = authRequiredToolNames.filter((name) => !authorizedTools.includes(name));
        return availableDomains.filter((domain) => {
            if (domain.customServices?.some((svc) => activeAuthRequired.includes(svc.name))) {
                return false;
            }
            if (selectedDomains?.length > 0) {
                return selectedDomains.includes(domain.name);
            }
            return true;
        });
    }, [availableDomains, authRequiredToolNames, authorizedTools, selectedDomains]);

    const onFeedbackDone = () => {
        setShowFeedback(false);
        setSelectedAnswer('');
        setSelectedQuestion('');
        toast('success', t('success'), t('feedback.thank_you'));
    };

    const giveFeedback = (item: IDialogItem, index: number, type: 'up' | 'down') => {
        let answer = '';
        let question = '';

        if (item.type === 'response') {
            const prevRequest = findPreviousRequest(dialog, index);
            answer = findResolution(item);

            if (prevRequest) {
                question = prevRequest.parts[0].message;
            }
        }

        setShowFeedback(true);
        setSelectedAnswer(answer);
        setSelectedQuestion(question);
        setFeedbackType(type);
    };

    const scrollToBottom = () => {
        if (containerRef.current) {
            containerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            });
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            scrollToBottom();
        }, 750);

        scrollToBottom();

        return () => {
            clearTimeout(timeoutId);
        };
    }, [JSON.stringify(dialog)]);

    const showFeedbackFunc = useCallback(
        (item: IDialogItem) => {
            if (item.type !== 'response' || loading) {
                return false;
            }

            if (item.uid.startsWith('prepend-context-')) {
                return false;
            }

            const hasInvalidRequest = item.parts.some((part) => part.type === 'invalid-request');
            if (hasInvalidRequest) {
                return false;
            }

            const hasMarkdown = item.parts.some((part) => part.type === 'markdown' || part.type === 'html');

            if (!hasMarkdown) {
                const allJsonBlocks = item.parts
                    .filter((part) => part.type === 'json' && part.jsonBlocks)
                    .flatMap((part) => part.jsonBlocks!);

                if (allJsonBlocks.length > 0) {
                    const onlyToolSelectOrNextBlock = allJsonBlocks.every(
                        (block) =>
                            block.data_type === 'tool_selector' ||
                            block.data_type === 'next-block' ||
                            block.data_type === 'auth-required' ||
                            block.data_type === 'proceed-button',
                    );

                    if (onlyToolSelectOrNextBlock) {
                        return false;
                    }
                }
            }

            return true;
        },
        [loading],
    );

    const updateMessage = useCallback(
        (index: number, updatedItem: IDialogItem) => {
            if (!onUpdateDialog) return;

            if (index < 0 || index >= dialog.length) {
                console.error('Invalid index for updateMessage:', index, 'dialog length:', dialog.length);
                return;
            }

            if (dialog[index].uid !== updatedItem.uid) {
                console.error('UID mismatch in updateMessage. Expected:', dialog[index].uid, 'Got:', updatedItem.uid);
                return;
            }

            const updatedDialog = [...dialog];
            updatedDialog[index] = updatedItem;

            onUpdateDialog(updatedDialog);
        },
        [dialog, onUpdateDialog],
    );

    const removeMessage = useCallback(
        (index: number) => {
            if (!onUpdateDialog) return;

            if (index < 0 || index >= dialog.length) {
                console.error('Invalid index for removeMessage:', index, 'dialog length:', dialog.length);
                return;
            }

            const updatedDialog = dialog.filter((_, i) => i !== index);

            const hasOtherAuthRequired = updatedDialog.some((item) => {
                return item.parts.some((part) => {
                    if (part.type === 'json' && part.jsonBlocks) {
                        return part.jsonBlocks.some((block) => block.data_type === 'auth-required');
                    }
                    return false;
                });
            });

            if (!hasOtherAuthRequired) {
                removeAuthMessages(updatedDialog);
            } else {
                onUpdateDialog(updatedDialog);
            }
        },
        [dialog, onUpdateDialog],
    );

    const removeAuthMessages = useCallback(
        (currentDialog?: IDialogItem[]) => {
            if (!onUpdateDialog) return;

            const dialog2update = currentDialog ? currentDialog : dialog;

            const allAuthRequiredTools = new Set<string>();
            dialog2update.forEach((item) => {
                item.parts.forEach((part) => {
                    if (part.type === 'json' && part.jsonBlocks) {
                        part.jsonBlocks.forEach((block) => {
                            if (block.data_type === 'auth-required') {
                                block.data.forEach((authData) => {
                                    allAuthRequiredTools.add(authData.toolName);
                                });
                            }
                        });
                    }
                });
            });

            const currentAuthorizedTools = authorizedToolsRef.current;
            const excludedTools = Array.from(allAuthRequiredTools).filter(
                (toolName) => !currentAuthorizedTools.includes(toolName),
            );

            const updatedDialog = dialog2update.filter((item) => {
                const hasAuthOrProceedButton = item.parts.some((part) => {
                    if (part.type === 'json' && part.jsonBlocks) {
                        return part.jsonBlocks.some(
                            (block) => block.data_type === 'auth-required' || block.data_type === 'proceed-button',
                        );
                    }
                    return false;
                });
                return !hasAuthOrProceedButton;
            });

            onUpdateDialog(updatedDialog);

            if (onResendRequest) {
                onResendRequest(excludedTools);
            }
        },
        [dialog, onUpdateDialog, onResendRequest],
    );

    const drawMessage = useCallback(
        (currentMessage: IDialogItem, index: number) => {
            return (
                <Message
                    key={currentMessage.uid}
                    index={index}
                    item={currentMessage}
                    dialog={dialog}
                    setSelectedMessage={setSelectedMessage}
                    showFeedback={showFeedbackFunc(currentMessage)}
                    loading={loading}
                    showSearchRequest={showSearchRequest}
                    botIcon={botIcon}
                    loadMore={loadMore}
                    onUpdateMessage={updateMessage}
                    onRemoveMessage={removeMessage}
                    onRemoveAuthMessages={removeAuthMessages}
                    useInfoButton={useInfoButton}
                    onAction={onAction}
                />
            );
        },
        [
            dialog,
            setSelectedMessage,
            showFeedbackFunc,
            loading,
            showSearchRequest,
            botIcon,
            loadMore,
            updateMessage,
            removeMessage,
            removeAuthMessages,
            useInfoButton,
            customStyles,
        ],
    );

    const getClassName = (item: IDialogItem, index: number) => {
        return item.type == 'response' && selectedMessage === index ? 'itemSelected' : '';
    };

    const getThinkingContent = useCallback((item: IDialogItem): ThinkingContent | null => {
        if (item.type !== 'response') return null;

        for (const part of item.parts) {
            if (part.type === 'json' && part.jsonBlocks) {
                for (const block of part.jsonBlocks) {
                    if (block.data_type === 'thinking' || block.data_type === 'stream_thinking') {
                        return block as ThinkingContent;
                    }
                }
            }
        }
        return null;
    }, []);

    const handleShowThinking = (item: IDialogItem) => {
        const thinkingContent = getThinkingContent(item);
        if (thinkingContent) {
            setSelectedThinkingContent(thinkingContent);
            setShowThinking(true);
        }
    };

    return (
        <Box sx={styles.container}>
            <Stack sx={styles.dialogList} ref={containerRef}>
                {dialog.map((item, index) => {
                    const allBlocks = item.parts
                        .filter((p) => p.type === 'json' && p.jsonBlocks)
                        .flatMap((p) => p.jsonBlocks!);
                    if (
                        allBlocks.length > 0 &&
                        allBlocks.every((b) => b.data_type === 'proceed-button') &&
                        proceedButtonFilteredDomains.length === 0
                    ) {
                        return null;
                    }
                    return (
                        <Stack
                            sx={styles.messageContainer}
                            className={`${item.type} ${getClassName(item, index)}`}
                            key={item.uid}
                        >
                            {drawMessage(item, index)}
                            {(showFeedbackFunc(item) || (!loading && getThinkingContent(item))) && (
                                <Stack className={'hover-actions'} direction={'row'} sx={styles.hoverActionContainer}>
                                    {showFeedbackFunc(item) ? (
                                        <Stack direction={'row'} spacing={1} sx={styles.hoverAction}>
                                            <Box
                                                sx={styles.hoverActionIcon}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    giveFeedback(item, index, 'up');
                                                }}
                                            >
                                                <IconMi icon="thumbs-up" sx={{ fontSize: '16px' }} />
                                            </Box>
                                            <Box
                                                sx={styles.hoverActionIcon}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    giveFeedback(item, index, 'down');
                                                }}
                                            >
                                                <IconMi icon="thumbs-down" sx={{ fontSize: '16px' }} />
                                            </Box>
                                        </Stack>
                                    ) : (
                                        <Box />
                                    )}
                                    {!loading && getThinkingContent(item) && (
                                        <Box
                                            sx={styles.showThinkingButton}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleShowThinking(item);
                                            }}
                                        >
                                            {t('thinking.button')}
                                        </Box>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    );
                })}
            </Stack>
            {showFeedback && (
                <Feedback
                    show={showFeedback}
                    onHide={() => setShowFeedback(false)}
                    id={id}
                    question={selectedQuestion}
                    answer={selectedAnswer}
                    onFeedbackDone={onFeedbackDone}
                    type={feedbackType}
                />
            )}
            {showThinking && selectedThinkingContent && (
                <ThinkingPopup
                    show={showThinking}
                    onHide={() => setShowThinking(false)}
                    thinkingContent={selectedThinkingContent}
                />
            )}
        </Box>
    );
}
